import type { SystemSettingKeyTypeModuleAliases } from '@openbox/shared-types'

/**
 * Grammar Configuration for Module Aliases
 *
 * This module provides the infrastructure to generate grammatically correct
 * Spanish text for module labels based on singular, plural, and gender inputs.
 *
 * The types are inferred from the templates, so you only need to add/remove
 * keys in the TEMPLATES constants - the types will update automatically.
 *
 * @example
 * ```ts
 * import { GRAMMAR, CUSTOMER_TEMPLATES, CustomerMessages } from '~/config/grammar.config'
 * ```
 */

/**
 * Gender type for Spanish grammar
 * - 'm' = masculine (el, un, nuevo, este)
 * - 'f' = feminine (la, una, nueva, esta)
 */
export type GrammarGender = 'm' | 'f'

/**
 * Input structure for module alias configuration
 * This matches the structure returned by the API in MODULE_ALIASES
 * Re-exported from @openbox/shared-types for convenience
 */
export type ModuleAliasInput = SystemSettingKeyTypeModuleAliases[string]

/**
 * Grammar variants for each gender in Spanish
 * These are the building blocks used to construct grammatically correct sentences
 */
export interface GrammarVariants {
  /** Article: "el" (m) or "la" (f) */
  el: string
  /** Plural article: "los" (m) or "las" (f) */
  los: string
  /** Contraction "de + el": "del" (m) or "de la" (f) */
  del: string
  /** Contraction "a + el": "al" (m) or "a la" (f) */
  al: string
  /** Indefinite article: "un" (m) or "una" (f) */
  un: string
  /** Demonstrative: "este" (m) or "esta" (f) */
  este: string
  /** Adjective "nuevo": "Nuevo" (m) or "Nueva" (f) - capitalized */
  nuevo: string
  /** Quantifier "todos": "Todos" (m) or "Todas" (f) - capitalized */
  todos: string
}

/**
 * Grammar variants by gender
 * Provides all the Spanish grammatical variations needed for text generation
 */
export const GRAMMAR: Record<GrammarGender, GrammarVariants> = {
  m: {
    el: 'el',
    los: 'los',
    del: 'del',
    al: 'al',
    un: 'un',
    este: 'este',
    nuevo: 'Nuevo',
    todos: 'Todos',
  },
  f: {
    el: 'la',
    los: 'las',
    del: 'de la',
    al: 'a la',
    un: 'una',
    este: 'esta',
    nuevo: 'Nueva',
    todos: 'Todas',
  },
}

/**
 * Message templates using placeholders
 *
 * Available placeholders:
 * - {s} : singular lowercase (e.g., "asistente")
 * - {S} : singular as-is (e.g., "Asistente")
 * - {p} : plural lowercase (e.g., "asistentes")
 * - {P} : plural as-is (e.g., "Asistentes")
 * - {el}, {los}, {del}, {al}, {un}, {este} : grammar variants (lowercase)
 * - {nuevo}, {todos} : grammar variants (capitalized)
 * - {nuevo_lower} : "nuevo" or "nueva" in lowercase
 */

// =============================================================================
// BASE MODULE (minimum required for all modules)
// =============================================================================

/**
 * Base templates - minimum required for all modules
 * Add/remove keys here and BaseModuleMessages type will update automatically
 */
export const BASE_TEMPLATES = {
  meta: {
    /** Singular form - e.g., "Asistente" */
    singular: '{S}',
    /** Plural form for menu - e.g., "Asistentes" */
    plural: '{P}',
  },
} as const

/**
 * Base messages type - hierarchical structure
 * Contains minimum required messages for any module
 */
export type BaseModuleMessages = {
  meta: {
    singular: string
    plural: string
  }
}

// =============================================================================
// CUSTOMER MODULE (full set of messages for customers/clients)
// =============================================================================

/**
 * Customer module templates - hierarchical structure
 * Add/remove keys here and CustomerMessages type will update automatically
 *
 * Templates for generating all customer-specific messages including:
 * - Meta: Module identity (singular/plural)
 * - Titles: Page headers
 * - Navigation: Sidebar menu items
 * - Actions: Row action labels
 * - Buttons: Confirmation button texts
 * - Confirmations: Dialog texts
 * - Errors: Frontend and backend error messages
 * - Success: Frontend and backend success messages
 * - Labels: UI labels and descriptions
 */
export const CUSTOMER_TEMPLATES = {
  /** Module identity */
  meta: {
    singular: '{S}',
    plural: '{P}',
  },

  /** Page titles */
  titles: {
    new: '{nuevo} {S}',
    edit: 'Editar {S}',
    list: 'Listado de {p}',
  },

  /** Sidebar navigation labels */
  navigation: {
    new: '{nuevo} {s}',
    list: 'Listado de {p}',
  },

  /** Row action labels */
  actions: {
    edit: 'Editar {s}',
    delete: 'Eliminar {S}',
    activate: 'Activar {S}',
    deactivate: 'Desactivar {S}',
  },

  /** Confirmation button texts */
  buttons: {
    yesCreate: 'Sí, crear',
    yesUpdate: 'Sí, actualizar',
    yesSave: 'Sí, guardar',
    yesDelete: 'Sí, eliminar',
    yesActivate: 'Sí, activar',
    yesDeactivate: 'Sí, desactivar',
    cancel: 'Cancelar',
    noExit: 'No, salir',
  },

  /** Confirmation dialog texts */
  confirmations: {
    new: '¿Deseas crear {un} {nuevo_lower} {s}?',
    edit: '¿Estás seguro de actualizar {este} {s}?',
    save: '¿Estás seguro de guardar {este} {s}?',
    delete:
      '¿Estás seguro de que deseas eliminar {al} {s} "{name}"? Esta acción no se puede deshacer.',
    toggle: '¿Deseas {action} {al} {s} "{name}"?',
  },

  /** Error messages */
  errors: {
    frontend: {
      idMissing: 'ID de {s} no proporcionado',
    },
    backend: {
      notFound: 'No se pudo cargar {el} {s}. Por favor, intente nuevamente.',
      fetchError: 'Error al obtener el listado de {p}',
    },
  },

  /** Success messages */
  success: {
    frontend: {},
    backend: {
      created: '{S} creado exitosamente',
      updated: '{S} actualizado exitosamente',
      deleted: '{S} eliminado exitosamente',
      activated: '{S} activado exitosamente',
      deactivated: '{S} desactivado exitosamente',
    },
  },

  /** UI labels and descriptions */
  labels: {
    details: 'Detalles {del} {s}',
    type: 'Tipo de {s}',
    all: '{todos} {los} {p}',
    allowCreate: 'Permitir creación de {p}',
  },
} as const

/**
 * Customer messages type - hierarchical structure
 * Contains all texts specific to the customers/clients module
 */
export type CustomerMessages = {
  meta: {
    singular: string
    plural: string
  }
  titles: {
    new: string
    edit: string
    list: string
  }
  navigation: {
    new: string
    list: string
  }
  actions: {
    edit: string
    delete: string
    activate: string
    deactivate: string
  }
  buttons: {
    yesCreate: string
    yesUpdate: string
    yesSave: string
    yesDelete: string
    yesActivate: string
    yesDeactivate: string
    cancel: string
    noExit: string
  }
  confirmations: {
    new: string
    edit: string
    save: string
    delete: string
    toggle: string
  }
  errors: {
    frontend: {
      idMissing: string
    }
    backend: {
      notFound: string
      fetchError: string
    }
  }
  success: {
    frontend: Record<string, never>
    backend: {
      created: string
      updated: string
      deleted: string
      activated: string
      deactivated: string
    }
  }
  labels: {
    details: string
    type: string
    all: string
    allowCreate: string
  }
}
